import { useActionConfirmation } from '../components/ActionFeedbackProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, DollarSign, FileDown, Home, Plus, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useTheme from '../hooks/useTheme';
import api from '../lib/api';
import { useTranslation } from 'react-i18next';
import { makePriceWorkbook } from '../utils/priceWorkbook';
import PageHeading from '../components/PageHeading';

const PRICE_KEY_PREFIX = 'sgi-price-v1:';
const GENERAL_PRICE_CLIENT_ID = '__general_price_list__';

const DEFAULT_BOXES = [
  { name: 'Panza', brands: ['Blue Ribbon', 'ComCarne', 'CreekStone', 'Demkota', 'Excel', 'KilKoy', 'M.C.Herd', 'National', 'Nebraska', 'Teys'] },
  { name: 'Tripa', brands: ['Blue Ribbon', 'CreekStone', 'Demkota', 'National'] },
  { name: 'Libro', brands: ['Ñuble', 'San Martin', 'Temuco'] },
  { name: 'Panal', brands: ['Montecillos', 'Ñuble', 'San Martin'] },
  { name: 'Lengua', brands: ['Anglo', 'Concepcion', 'Frigol'] },
];

const DEFAULT_BULK = [
  'Cuero Blanco', 'Hígado', 'Lengua', 'Libro', 'Médula', 'Molleja', 'Morena',
  'Natural', 'Nixtacal', 'Panal', 'Panza Cocida', 'Panza Cruda',
  'Panza Pedacera', 'Pata Entera', 'Pata Hueso', 'Peróxido', 'Piel', 'Químico',
];

const DEFAULT_PIECE = [
  'B Nixtacal', 'B Químico', 'Botas', 'Cabeza', 'Chamfaina', 'Mandil',
  'P Peróxido', 'Res', 'Seso', 'Tambo',
];

const RES_SIZES = ['Extra chica', 'Chica', 'Mediana', 'Grande', 'Extra grande'];
const RES_SIZE_TRANSLATION_KEYS = {
  'Extra chica': 'sizeExtraSmall',
  Chica: 'sizeSmall',
  Mediana: 'sizeMedium',
  Grande: 'sizeLarge',
  'Extra grande': 'sizeExtraLarge',
};
const BOX_COLORS = [
  'bg-fuchsia-700', 'bg-sky-600', 'bg-lime-600', 'bg-yellow-400 text-black', 'bg-red-600',
];

const normalizeText = (value = '') => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const itemKey = (item) => JSON.stringify([
  item.section,
  normalizeText(item.product),
  normalizeText(item.brand || ''),
  normalizeText(item.variant || ''),
]);

const encodePriceName = (item) => `${PRICE_KEY_PREFIX}${encodeURIComponent(JSON.stringify({
  section: item.section,
  product: item.product,
  ...(item.brand ? { brand: item.brand } : {}),
  ...(item.variant ? { variant: item.variant } : {}),
}))}`;

const decodePriceName = (value = '') => {
  if (!value.startsWith(PRICE_KEY_PREFIX)) return null;
  try {
    return JSON.parse(decodeURIComponent(value.slice(PRICE_KEY_PREFIX.length)));
  } catch {
    return null;
  }
};

const makeInitialCatalog = () => ({
  boxes: DEFAULT_BOXES.map((item) => ({ ...item, brands: [...item.brands] })),
  bulk: DEFAULT_BULK.map((name) => ({ name })),
  piece: DEFAULT_PIECE.map((name) => ({ name })),
});

const addStoredItemsToCatalog = (catalog, rows) => {
  const next = {
    boxes: catalog.boxes.map((item) => ({ ...item, brands: [...item.brands] })),
    bulk: catalog.bulk.map((item) => ({ ...item })),
    piece: catalog.piece.map((item) => ({ ...item })),
  };

  rows.forEach((row) => {
    if (row.catalogHidden) return;
    const item = decodePriceName(row.productName || '');
    if (!item || !['boxes', 'bulk', 'piece'].includes(item.section)) return;

    if (item.section === 'boxes') {
      let product = next.boxes.find((entry) => normalizeText(entry.name) === normalizeText(item.product));
      if (!product) {
        product = { name: item.product, brands: [] };
        next.boxes.push(product);
      }
      if (item.brand && !product.brands.some((brand) => normalizeText(brand) === normalizeText(item.brand))) {
        product.brands.push(item.brand);
      }
      return;
    }

    const collection = next[item.section];
    if (!collection.some((entry) => normalizeText(entry.name) === normalizeText(item.product))) {
      collection.push({ name: item.product });
    }
  });

  return next;
};

const PricesScreen = () => {
  const [clients, setClients] = useState([]);
  const [prices, setPrices] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showGeneralPrices, setShowGeneralPrices] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('az');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const authRaw = localStorage.getItem('auth') || '{}';
  const auth = JSON.parse(authRaw);
  const userId = auth?.username || '';

  const baseBtn = 'inline-flex items-center justify-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-gray-700 disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-gray-200';
  const lineClass = theme === 'dark' ? 'border-gray-600 bg-gray-900 text-white' : 'border-gray-300 bg-white text-black';
  const inputClass = `w-full min-w-[76px] rounded border px-2 py-1.5 text-center outline-none focus:ring-2 focus:ring-blue-500 ${theme === 'dark' ? 'border-gray-600 bg-gray-800 text-white placeholder:text-gray-500' : 'border-gray-300 bg-white text-black placeholder:text-gray-400'}`;

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api.get('/clients');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(t('errorLoadClients'));
    }
  }, [t]);

  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/prices');
      const rows = Array.isArray(data)
        ? data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [];
      setPrices(rows);
    } catch (err) {
      console.error(err);
      setError(t('errorLoadPrices'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
    fetchPrices();
  }, [fetchClients, fetchPrices]);

  const handleExportBlankTemplate = () => {
    const catalogTemplate = makeInitialCatalog();
    const workbook = makePriceWorkbook({
      clientName: '',
      ...catalogTemplate,
      sizes: RES_SIZES,
      getPrice: () => '',
      includeClientName: false,
    });
    const url = URL.createObjectURL(workbook);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Plantilla_Precios.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const selectedClientId = selectedClient?.id || selectedClient?._id || (showGeneralPrices ? GENERAL_PRICE_CLIENT_ID : null);
  const clientPrices = useMemo(() => selectedClientId
    ? prices.filter((price) => String(price.clientId) === String(selectedClientId))
    : [], [prices, selectedClientId]);
  const catalog = useMemo(() => addStoredItemsToCatalog(makeInitialCatalog(), clientPrices), [clientPrices]);

  const { structuredPriceMap, hiddenCatalogItems } = useMemo(() => {
    const structuredPriceMap = {};
    const hiddenCatalogItems = new Set();
    clientPrices.forEach((price) => {
      const item = decodePriceName(price.productName || '');
      if (item && price.catalogHidden) hiddenCatalogItems.add(itemKey(item));
      else if (item) structuredPriceMap[itemKey(item)] = price;
    });
    return { structuredPriceMap, hiddenCatalogItems };
  }, [clientPrices]);

  const dirtyCount = Object.keys(drafts).length;

  const updatePriceDraft = (item, value) => {
    const key = itemKey(item);
    setDrafts((current) => ({ ...current, [key]: { item, value } }));
    setSavedMessage('');
  };

  const getDraftValue = (item) => {
    const key = itemKey(item);
    if (Object.prototype.hasOwnProperty.call(drafts, key)) return drafts[key].value;
    const record = structuredPriceMap[key];
    return record?.price === undefined || record?.price === null ? '' : String(record.price);
  };

  const handleExportTemplate = () => {
    const visible = (section, product, brand = '', variant = '') => !hiddenCatalogItems.has(itemKey({ section, product, brand, variant }));
    const boxes = catalog.boxes
      .filter((product) => visible('boxes', product.name))
      .map((product) => ({ ...product, brands: product.brands.filter((brand) => visible('boxes', product.name, brand)) }));
    const bulk = catalog.bulk.filter((product) => visible('bulk', product.name));
    const piece = catalog.piece.filter((product) => visible('piece', product.name));
    const workbook = makePriceWorkbook({
      clientName: selectedClient?.name || '', boxes, bulk, piece, sizes: RES_SIZES, getPrice: getDraftValue,
    });
    const safeName = (selectedClient?.name || 'Cliente').replace(/[\\/:*?"<>|]/g, '').trim();
    const url = URL.createObjectURL(workbook);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Precios_${safeName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleSaveChanges = async () => {
    if (!selectedClientId || dirtyCount === 0) return;

    for (const { value } of Object.values(drafts)) {
      if (value.trim() !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
        setError(t('priceMustBeNonNegative'));
        return;
      }
    }
    setBusyAction(true);
    setError('');
    setSavedMessage('');
    const remainingDrafts = { ...drafts };
    let failed = false;

    for (const [key, draft] of Object.entries(drafts)) {
      const existing = structuredPriceMap[key];
      const value = draft.value.trim();
      try {
        if (value === '') {
          if (existing?.catalogItem) {
            await api.put(`/prices/${existing.id || existing._id}`, { ...existing, price: null, catalogItem: true });
          } else if (existing) {
            await api.delete(`/prices/${existing.id || existing._id}`);
          }
        } else {
          const payload = {
            userId,
            clientId: selectedClientId,
            productName: encodePriceName(draft.item),
            price: Number(value),
            order: existing?.order ?? prices.length,
          };
          if (existing) await api.put(`/prices/${existing.id || existing._id}`, { ...existing, ...payload });
          else await api.post('/prices', payload);
        }
        delete remainingDrafts[key];
      } catch (err) {
        console.error(err);
        failed = true;
      }
    }

    setDrafts(remainingDrafts);
    await fetchPrices();
    setBusyAction(false);
    if (failed) setError(t('errorSavePrices'));
    else setSavedMessage(t('pricesSaved'));
  };

  const addCatalogProduct = (section) => {
    const label = window.prompt(t('enterPriceProductName'))?.trim();
    if (!label) return;
    const restoredItem = { section, product: label };
    const restoredMarker = clientPrices.find((row) => row.catalogHidden
      && itemKey(decodePriceName(row.productName || '') || {}) === itemKey(restoredItem));
    if (restoredMarker) {
      setBusyAction(true);
      api.put(`/prices/${restoredMarker.id || restoredMarker._id}`, { ...restoredMarker, catalogHidden: false, catalogItem: true, price: null })
        .then(() => fetchPrices())
        .catch((err) => { console.error(err); setError(t('errorRestoreCatalogItem')); })
        .finally(() => setBusyAction(false));
      setError('');
      return;
    }
    const currentItems = catalog[section];
    if (currentItems.some((item) => normalizeText(item.name) === normalizeText(label))) {
      setError(t('productAlreadyExists'));
      return;
    }
    setBusyAction(true);
    api.post('/prices', {
      userId,
      clientId: selectedClientId,
      productName: encodePriceName(restoredItem),
      price: null,
      order: prices.length,
      catalogItem: true,
    }).then(({ data }) => {
      setPrices((current) => [...current, data]);
      setError('');
    }).catch((err) => {
      console.error(err);
      setError(t('errorAddCatalogItem'));
    }).finally(() => setBusyAction(false));
  };

  const addBrand = (productName) => {
    const brand = window.prompt(t('enterPriceBrandName'))?.trim();
    if (!brand) return;
    const restoredItem = { section: 'boxes', product: productName, brand };
    const restoredMarker = clientPrices.find((row) => row.catalogHidden
      && itemKey(decodePriceName(row.productName || '') || {}) === itemKey(restoredItem));
    if (restoredMarker) {
      setBusyAction(true);
      api.put(`/prices/${restoredMarker.id || restoredMarker._id}`, { ...restoredMarker, catalogHidden: false, catalogItem: true, price: null })
        .then(() => fetchPrices())
        .catch((err) => { console.error(err); setError(t('errorRestoreCatalogItem')); })
        .finally(() => setBusyAction(false));
      setError('');
      return;
    }
    const product = catalog.boxes.find((item) => normalizeText(item.name) === normalizeText(productName));
    if (product?.brands.some((existing) => normalizeText(existing) === normalizeText(brand))) {
      setError(t('brandAlreadyExists'));
      return;
    }
    setBusyAction(true);
    api.post('/prices', {
      userId,
      clientId: selectedClientId,
      productName: encodePriceName(restoredItem),
      price: null,
      order: prices.length,
      catalogItem: true,
    }).then(({ data }) => {
      setPrices((current) => [...current, data]);
      setError('');
    }).catch((err) => {
      console.error(err);
      setError(t('errorAddCatalogItem'));
    }).finally(() => setBusyAction(false));
  };

  const deleteCatalogItem = async (item, description) => {
    if (!selectedClientId || !await confirmAction(t('confirmDeleteCatalogItem', { item: description }))) return;
    const encodedName = encodePriceName(item);
    const existingMarker = clientPrices.find((row) => row.catalogHidden
      && itemKey(decodePriceName(row.productName || '') || {}) === itemKey(item));
    const matchingPrices = clientPrices.filter((row) => {
      const stored = decodePriceName(row.productName || '');
      if (!stored || row.catalogHidden || stored.section !== item.section
        || normalizeText(stored.product) !== normalizeText(item.product)) return false;
      return item.brand ? normalizeText(stored.brand || '') === normalizeText(item.brand) : true;
    });

    setBusyAction(true);
    setError('');
    try {
      if (!existingMarker) {
        const { data: marker } = await api.post('/prices', {
          userId,
          clientId: selectedClientId,
          productName: encodedName,
          price: null,
          order: prices.length,
          catalogHidden: true,
          catalogItem: true,
        });
        setPrices((current) => [...current, marker]);
      }
      for (const price of matchingPrices) {
        await api.delete(`/prices/${price.id || price._id}`);
      }
      await fetchPrices();
      setDrafts((current) => {
        const next = { ...current };
        Object.keys(next).forEach((key) => {
          const draft = next[key];
          if (draft.item.section === item.section
            && normalizeText(draft.item.product) === normalizeText(item.product)
            && (!item.brand || normalizeText(draft.item.brand || '') === normalizeText(item.brand))) delete next[key];
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setError(t('errorDeleteCatalogItem'));
    } finally {
      setBusyAction(false);
    }
  };

  const handleBack = async () => {
    if (dirtyCount > 0 && !window.confirm(t('discardUnsavedPriceChanges'))) return;
    setDrafts({});
    setSelectedClient(null);
    setShowGeneralPrices(false);
    setError('');
    setSavedMessage('');
  };

  const filteredClients = clients
    .filter((client) => (client.name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortOption === 'az'
      ? (a.name || '').localeCompare(b.name || '')
      : (b.name || '').localeCompare(a.name || ''));

  const priceInput = (item, placeholder = '—') => (
    <input
      type="number"
      min="0"
      step="0.01"
      inputMode="decimal"
      value={getDraftValue(item)}
      onChange={(event) => updatePriceDraft(item, event.target.value)}
      aria-label={`${item.product}${item.brand ? `, ${item.brand}` : ''}${item.variant ? `, ${item.variant}` : ''}`}
      placeholder={placeholder}
      className={inputClass}
    />
  );

  const renderBoxTable = () => {
    const boxProducts = catalog.boxes.filter((item) => !hiddenCatalogItems.has(itemKey({ section: 'boxes', product: item.name })));
    const visibleBrands = (product) => product.brands.filter((brand) => !hiddenCatalogItems.has(itemKey({ section: 'boxes', product: product.name, brand })));
    const maxBrandCount = Math.max(1, ...boxProducts.map((item) => visibleBrands(item).length));
    return (
      <div className={`overflow-x-auto rounded-lg border ${lineClass}`}>
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead>
            <tr>
              {boxProducts.map((product, index) => (
                <th key={product.name} colSpan={2} className={`border border-gray-400 px-2 py-2 text-center font-bold uppercase ${BOX_COLORS[index % BOX_COLORS.length]}`}>
                  <div className="flex items-center justify-center gap-2">
                    <span>{product.name.toLocaleUpperCase('es')}</span>
                    <button type="button" onClick={() => deleteCatalogItem({ section: 'boxes', product: product.name }, product.name)} disabled={busyAction}
                      title={t('deleteProduct')} aria-label={`${t('deleteProduct')}: ${product.name}`} className="rounded p-1 hover:bg-black/10 disabled:opacity-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
            <tr className="bg-gray-300 text-gray-900 dark:bg-gray-700 dark:text-white">
              {boxProducts.map((product) => (
                <React.Fragment key={`${product.name}-subhead`}>
                  <th className="border border-gray-400 px-2 py-1">{t('brand')}</th>
                  <th className="border border-gray-400 px-2 py-1">{t('pricePerKg')}</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxBrandCount }).map((_, rowIndex) => (
              <tr key={`brand-row-${rowIndex}`}>
                {boxProducts.map((product) => {
                  const brand = visibleBrands(product)[rowIndex];
                  if (!brand) return <React.Fragment key={`${product.name}-empty-${rowIndex}`}><td className="border border-gray-400 px-2 py-1" /><td className="border border-gray-400 px-2 py-1" /></React.Fragment>;
                  const item = { section: 'boxes', product: product.name, brand };
                  return (
                    <React.Fragment key={`${product.name}-${brand}`}>
                      <td className="border border-gray-400 px-2 py-1 text-center font-semibold">
                        <div className="flex items-center justify-center gap-1">
                          <span>{brand.toLocaleUpperCase('es')}</span>
                          <button type="button" onClick={() => deleteCatalogItem({ section: 'boxes', product: product.name, brand }, `${brand} (${product.name})`)} disabled={busyAction}
                            title={t('deleteBrand')} aria-label={`${t('deleteBrand')}: ${brand}`} className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="border border-gray-400 px-2 py-1">{priceInput(item)}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              {boxProducts.map((product) => (
                <td key={`${product.name}-add-brand`} colSpan={2} className="border border-gray-400 p-2 text-center">
                  <button type="button" onClick={() => addBrand(product.name)} disabled={busyAction} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-300">
                    <Plus size={14} /> {t('addBrand')}
                  </button>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderBulkTable = () => (
    <div className={`overflow-x-auto rounded-lg border ${lineClass}`}>
      <table className="w-full min-w-[310px] border-collapse text-sm">
        <thead className="bg-gray-300 text-gray-900 dark:bg-gray-700 dark:text-white">
          <tr>
            <th className="border border-gray-400 px-3 py-2 text-left">{t('productName')}</th>
            <th className="border border-gray-400 px-3 py-2">{t('pricePerKg')}</th>
          </tr>
        </thead>
        <tbody>
          {catalog.bulk.filter((item) => !hiddenCatalogItems.has(itemKey({ section: 'bulk', product: item.name }))).map((product) => (
            <tr key={product.name}>
              <td className="border border-gray-400 px-3 py-1.5 font-semibold">
                <div className="flex items-center justify-between gap-2"><span>{product.name.toLocaleUpperCase('es')}</span>
                  <button type="button" onClick={() => deleteCatalogItem({ section: 'bulk', product: product.name }, product.name)} disabled={busyAction} title={t('deleteProduct')} aria-label={`${t('deleteProduct')}: ${product.name}`} className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950"><Trash2 size={14} /></button>
                </div>
              </td>
              <td className="border border-gray-400 px-2 py-1.5">{priceInput({ section: 'bulk', product: product.name })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPieceTable = () => (
    <div className={`overflow-x-auto rounded-lg border ${lineClass}`}>
      <table className="w-full min-w-[360px] border-collapse text-sm">
        <thead className="bg-gray-300 text-gray-900 dark:bg-gray-700 dark:text-white">
          <tr>
            <th className="border border-gray-400 px-3 py-2 text-left">{t('productName')}</th>
            <th className="border border-gray-400 px-3 py-2 text-center">{t('pricePerPiece')}</th>
          </tr>
        </thead>
        <tbody>
          {catalog.piece.filter((item) => !hiddenCatalogItems.has(itemKey({ section: 'piece', product: item.name }))).map((product) => (
            <tr key={product.name}>
              <td className="border border-gray-400 px-3 py-1.5 font-semibold align-top">
                <div className="flex items-center justify-between gap-2"><span>{product.name.toLocaleUpperCase('es')}</span>
                  <button type="button" onClick={() => deleteCatalogItem({ section: 'piece', product: product.name }, product.name)} disabled={busyAction} title={t('deleteProduct')} aria-label={`${t('deleteProduct')}: ${product.name}`} className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950"><Trash2 size={14} /></button>
                </div>
              </td>
              <td className="border border-gray-400 px-2 py-1.5">
                {normalizeText(product.name) === 'res' ? (
                  <div className="grid grid-cols-5 gap-1">
                    {RES_SIZES.map((size) => (
                      <label key={size} className="min-w-0 text-center text-[10px] font-semibold leading-tight">
                        <span className="mb-1 block min-h-[24px]">{t(RES_SIZE_TRANSLATION_KEYS[size])}</span>
                        {priceInput({ section: 'piece', product: product.name, variant: size })}
                      </label>
                    ))}
                  </div>
                ) : priceInput({ section: 'piece', product: product.name })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="mb-4 flex flex-col items-center gap-3">
        <button onClick={() => navigate('/')} className={baseBtn}><Home size={16} /> {t('home')}</button>
        {!selectedClient && !showGeneralPrices && (
          <>
            <button onClick={handleExportBlankTemplate} className={baseBtn}><FileDown size={16} /> {t('exportBlankPriceTemplate')}</button>
            <button onClick={() => { setShowGeneralPrices(true); setDrafts({}); setError(''); setSavedMessage(''); }} className={baseBtn}>
              <DollarSign size={16} /> {t('generalWithPrice')}
            </button>
          </>
        )}
      </div>

      <PageHeading icon={<DollarSign size={26} />} title={t('prices')} />
      {error && <div role="alert" className="mx-auto mb-4 max-w-6xl rounded bg-red-600 p-3 text-white">{error}</div>}

      {!selectedClient && !showGeneralPrices && (
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('searchClient')}
              className={`flex-1 rounded-lg border px-4 py-2 shadow ${lineClass}`}
            />
            <select value={sortOption} onChange={(event) => setSortOption(event.target.value)} className={`rounded-lg border px-4 py-2 shadow ${lineClass}`}>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
            </select>
          </div>
          {loading ? <p className="text-center">{t('loading')}</p> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {filteredClients.map((client) => (
                <button
                  key={client.id || client._id}
                  onClick={() => { setShowGeneralPrices(false); setSelectedClient(client); setDrafts({}); setError(''); }}
                  className={`${baseBtn} min-h-12`}
                >
                  {client.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {(selectedClient || showGeneralPrices) && (
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
            <button onClick={handleBack} className={`${baseBtn} justify-self-center sm:justify-self-start`} disabled={busyAction}><ArrowLeft size={16} /> {t('back')}</button>
            <h2 className="text-center text-3xl font-extrabold leading-tight md:text-4xl">{showGeneralPrices ? t('generalWithPrice') : selectedClient.name}</h2>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-self-end">
              {dirtyCount > 0 && <span className="text-sm text-amber-700 dark:text-amber-300">{t('unsavedChangesCount', { count: dirtyCount })}</span>}
              {savedMessage && <span role="status" className="text-sm text-green-700 dark:text-green-300">{savedMessage}</span>}
              <button onClick={handleExportTemplate} className={baseBtn}><FileDown size={16} /> {t('exportPriceTemplate')}</button>
              <button onClick={handleSaveChanges} className={baseBtn} disabled={busyAction || dirtyCount === 0}>
                <Save size={16} /> {busyAction ? t('saving') : t('saveChanges')}
              </button>
            </div>
          </div>

          <section className={`rounded-xl border p-3 md:p-4 ${lineClass}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-xl font-extrabold tracking-wide">{t('boxesSection')}</h3>
                <p className="text-xs opacity-75">{t('pricePerKg')}</p>
              </div>
              <button onClick={() => addCatalogProduct('boxes')} disabled={busyAction} className="inline-flex items-center gap-1 rounded border border-current px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800">
                <Plus size={16} /> {t('addProduct')}
              </button>
            </div>
            {renderBoxTable()}
          </section>

          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
            <section className={`rounded-xl border p-3 md:p-4 ${lineClass}`}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xl font-extrabold tracking-wide">{t('bulkSection')}</h3>
                  <p className="text-xs opacity-75">{t('pricePerKg')}</p>
                </div>
                <button onClick={() => addCatalogProduct('bulk')} disabled={busyAction} className="inline-flex items-center gap-1 rounded border border-current px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800">
                  <Plus size={16} /> {t('addProduct')}
                </button>
              </div>
              {renderBulkTable()}
            </section>

            <section className={`rounded-xl border p-3 md:p-4 ${lineClass}`}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xl font-extrabold tracking-wide">{t('pieceSection')}</h3>
                  <p className="text-xs opacity-75">{t('pricePerPiece')}</p>
                </div>
                <button onClick={() => addCatalogProduct('piece')} disabled={busyAction} className="inline-flex items-center gap-1 rounded border border-current px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800">
                  <Plus size={16} /> {t('addProduct')}
                </button>
              </div>
              {renderPieceTable()}
            </section>
          </div>

        </div>
      )}
    </div>
  );
};

export default PricesScreen;
